export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

type RequestJsonArgs = {
  url: string;
  method?: HttpMethod;
  headers?: HeadersInit;
  body?: unknown;
  signal?: AbortSignal;
};

export async function requestJson<T>({
  url,
  method = 'GET',
  headers,
  body,
  signal,
}: RequestJsonArgs): Promise<T> {
  const res = await globalThis.fetch(url, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(headers ?? {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const parsedBody = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      typeof parsedBody === 'object' && parsedBody && 'message' in (parsedBody as any)
        ? String((parsedBody as any).message)
        : `Request failed with status ${res.status}`;

    throw new ApiError(message, res.status, parsedBody);
  }

  return parsedBody as T;
}

type RequestFormDataArgs = {
  url: string;
  method?: HttpMethod;
  headers?: HeadersInit;
  formData: FormData;
  signal?: AbortSignal;
};

export async function requestFormData<T>({
  url,
  method = 'POST',
  headers,
  formData,
  signal,
}: RequestFormDataArgs): Promise<T> {
  const res = await globalThis.fetch(url, {
    method,
    headers: {
      ...(headers ?? {}),
    },
    body: formData,
    signal,
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const parsedBody = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      typeof parsedBody === 'object' && parsedBody && 'message' in (parsedBody as any)
        ? String((parsedBody as any).message)
        : `Request failed with status ${res.status}`;

    throw new ApiError(message, res.status, parsedBody);
  }

  return parsedBody as T;
}
